import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import api, { setApiCompany } from "@/lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const SESSION_HINT = "tenx_session_hint";
const APP_PREFIXES = ["/app", "/portal", "/admin", "/choose-plan"];
// Public marketing pages only probe /auth/me when this browser has signed in before (avoids 401 console noise).
const shouldProbeSession = () =>
  localStorage.getItem(SESSION_HINT) === "1" || APP_PREFIXES.some((p) => window.location.pathname.startsWith(p));

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async (force = false) => {
    if (!force && !shouldProbeSession()) {
      setUser(null); setCompany(null); setCompanies([]);
      setLoading(false);
      return null;
    }
    try {
      let data;
      try { ({ data } = await api.get("/auth/me")); }
      catch (err) {
        if (err?.response?.status !== 403) throw err;
        // A removed team membership must not strand an otherwise valid login.
        setApiCompany(null);
        ({ data } = await api.get("/auth/me"));
      }
      setApiCompany(data.user?.role === "founder" ? data.company?.id : null);
      setUser(data.user);
      setCompany(data.company || null);
      setCompanies(data.companies || []);
      sessionStorage.setItem("tenx_was_authed", "1");
      localStorage.setItem(SESSION_HINT, "1");
      return data.user;
    } catch (e) {
      setUser(null); setCompany(null); setCompanies([]);
      if (e?.response?.status === 401) localStorage.removeItem(SESSION_HINT);
      // If we previously had a session and are inside the app, show the expiry banner.
      const path = window.location.pathname;
      if (sessionStorage.getItem("tenx_was_authed") === "1" && (path.startsWith("/app") || path.startsWith("/portal"))) {
        sessionStorage.removeItem("tenx_was_authed");
        window.location.assign("/login?expired=1");
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadMe(); }, [loadMe]);

  const login = useCallback(async (email, password) => {
    setApiCompany(null);
    // Server sets an httpOnly cookie; no token is stored client-side.
    await api.post("/auth/login", { email, password });
    const user = await loadMe(true);
    return user;
  }, [loadMe]);

  const register = useCallback(async (payload) => {
    setApiCompany(null);
    await api.post("/auth/register", payload);
    const user = await loadMe(true);
    return user;
  }, [loadMe]);

  const requestMagicLink = useCallback(async (email) => {
    const { data } = await api.post("/auth/magic-link/request", { email });
    return data;
  }, []);

  const verifyMagicLink = useCallback(async (token) => {
    setApiCompany(null);
    await api.post("/auth/magic-link/verify", { token });
    const user = await loadMe(true);
    return user;
  }, [loadMe]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.error("Logout failed", e);
      toast.error("Couldn't reach the server to sign out - please try again.");
      return;
    }
    setUser(null); setCompany(null); setCompanies([]);
    setApiCompany(null);
    sessionStorage.removeItem("tenx_was_authed");
    localStorage.removeItem(SESSION_HINT);
    toast.success("Signed out");
  }, []);

  const switchCompany = useCallback(async (id) => {
    await api.post(`/founder/companies/${id}/activate`);
    // A clean navigation discards unsaved forms and in-flight state from the previous company.
    window.location.assign("/app");
  }, []);

  const value = useMemo(
    () => ({ user, company, companies, loading, login, register, logout, requestMagicLink, verifyMagicLink, switchCompany, reload: () => loadMe(true) }),
    [user, company, companies, loading, login, register, logout, requestMagicLink, verifyMagicLink, switchCompany, loadMe],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
